// AI Bookkeeper — pure presentation model for the guided year-end flow.
// Accounting conclusions remain owned by the existing backend read models.
(function registerYearEndAssistantFlow(global) {
  const list = (value) => Array.isArray(value) ? value : [];

  const CHECK_ROUTES = {
    'startup-basis': 'innstillinger',
    bank: 'year-end-bank',
    cash: 'arsavslutning-kontanter',
    purchases: 'arsavslutning-kjop',
    sales: 'arsavslutning-salg',
    'vat-returns': 'innsendinger',
    'vat-cleanup': 'arsavslutning-mva-kontoer',
    pension: 'arsavslutning-pensjon',
    inventory: 'arsavslutning-varelager',
    'periodisation-provisions': 'arsavslutning-periodiseringer',
    payables: 'arsavslutning-leverandorgjeld',
    receivables: 'arsavslutning-kundefordringer',
    'fixed-assets': 'arsavslutning-eiendeler',
    payroll: 'arsavslutning-lonn',
    'equity-review': 'arsavslutning-egenkapital',
    'final-review': 'arsavslutning-sluttkontroll',
    custom: 'arsavslutning-kontoplan',
    share: 'arsavslutning-aksjekapital',
    tax: 'skattemelding',
    result: 'fripostering',
    investments: 'investeringer',
  };

  const routeForYearEndCheck = (check) => CHECK_ROUTES[String(check?.id || '')] || 'arsavslutning';
  const yearEndShareBookedAmount = (check) => Number(String(check?.detail || '').match(/([0-9\s]+) kr/)?.[1]?.replace(/\s/g, '') || 0);
  const yearEndDefaultBankAccountCode = () => {
    const accounts = global.PlummeBackend?.appState?.settings?.bankAccounts || [];
    const picked = accounts.find((account) => account?.active !== false && (account.kind === 'operating' || account.invoiceDefault))
      || accounts.find((account) => account?.active !== false);
    return String(picked?.ledgerAccountCode || picked?.ledger || picked?.accountCode || picked?.code || '1920').trim() || '1920';
  };
  const workAreaContextForYearEndCheck = (check, options = {}) => {
    if (['vat-cleanup', 'pension', 'purchases', 'sales', 'inventory', 'periodisation-provisions', 'payables', 'receivables', 'fixed-assets', 'payroll', 'equity-review', 'final-review'].includes(String(check?.id || ''))) return { source: 'year-end', checkId: String(check.id), incomeYear: Number(options.year), returnRoute: options.appYear ? 'aiautopilot' : 'arsavslutning', returnSurface: options.appYear ? { kind: 'year-end', appYear: Number(options.appYear), task: { incomeYear: Number(options.year), appYear: Number(options.appYear), assistantStage: 'review', selectedCheckId: String(check.id) } } : null };
    if (['startup-basis', 'tax'].includes(String(check?.id || ''))) return { source: 'year-end', checkId: check.id, incomeYear: Number(options.year) };
    if (String(check?.id || '') === 'vat-returns') return { source: 'year-end', checkId: 'vat-returns', incomeYear: Number(options.year) };
    if (String(check?.id || '') === 'share') return {
      source: 'year-end',
      checkId: 'share',
      returnRoute: 'arsavslutning-aksjekapital',
      returnLabel: 'Aksjekapital',
      manualPostingPrefill: {
        fromAccountCode: '2000',
        toAccountCode: yearEndDefaultBankAccountCode(),
        amount: Math.max(0, 30000 - yearEndShareBookedAmount(check)),
        date: options.year ? `${Number(options.year)}-12-31` : '',
        description: 'Bokføring av aksjekapital',
      },
    };
    if (String(check?.id || '') === 'cash') return {
      source: 'year-end',
      checkId: 'cash',
      returnRoute: options.appYear ? 'aiautopilot' : 'arsavslutning',
      returnLabel: options.appYear ? 'AI Regnskapsfører' : 'Årsavslutning',
      returnSurface: options.appYear ? { kind: 'year-end', appYear: Number(options.appYear), task: { incomeYear: Number(options.year), appYear: Number(options.appYear), assistantStage: 'review', selectedCheckId: 'cash' } } : null,
    };
    if (String(check?.id || '') === 'bank') return {
      source: 'year-end',
      checkId: 'bank',
      incomeYear: Number(options.year),
      returnRoute: options.appYear ? 'aiautopilot' : 'arsavslutning',
      returnLabel: options.appYear ? 'AI Regnskapsfører' : 'Årsavslutning',
      returnSurface: options.appYear ? { kind: 'year-end', appYear: Number(options.appYear), task: { incomeYear: Number(options.year), appYear: Number(options.appYear), assistantStage: 'review', selectedCheckId: 'bank' } } : null,
    };
    return null;
  };
  const taskUsesYearEndSurface = (task) => ['skatt', 'arsregnskap'].includes(task?.kind)
    || ['taxSubmitted', 'annualAccountsSubmitted'].includes(task?.deadlineId);
  const routeUsesYearEndSurface = (route) => ['skattemelding', 'arsavslutning', 'arsregnskap'].includes(route);

  const buildYearEndAssistantFlow = ({ closing, tax, annual } = {}) => {
    const checks = list(closing?.checks);
    const grouped = global.YearClosingGroups.build(checks);
    const approved = checks.filter((check) => check?.status === 'ok');
    const notApplicable = checks.filter((check) => check?.status === 'na');
    const pending = checks.filter((check) => check?.status === 'todo');
    const questions = pending.filter((check) => check?.allowManualReview === true);
    const actions = pending.filter((check) => check?.allowManualReview !== true);
    const yearClosed = Boolean(closing?.status?.yearClosed);
    const taxSubmitted = Boolean(tax?.status?.submitted || tax?.submission?.submittedAt);
    const annualRequired = annual?.annualAccountsRequired;
    const annualSubmitted = Boolean(annual?.status?.submitted || annual?.process?.submittedAt);
    const stage = yearClosed
      ? 'submission'
      : ['summary', 'review', 'additions', 'final'].includes(closing?.progress?.assistantStage)
        ? closing.progress.assistantStage
        : 'summary';
    return {
      stage,
      checks,
      approved,
      notApplicable,
      pending,
      questions,
      actions,
      groups: grouped.groups,
      prerequisites: grouped.prerequisites,
      approvedGroups: grouped.groups.filter((group) => group.status === 'ok'),
      notApplicableGroups: grouped.groups.filter((group) => group.status === 'na'),
      pendingGroups: grouped.groups.filter((group) => group.status === 'todo'),
      allChecksClear: checks.length > 0
        && pending.length === 0
        && grouped.prerequisites.every((check) => check.status !== 'todo')
        && grouped.groups.every((group) => group.status !== 'todo')
        && grouped.ungrouped.length === 0,
      noMoreAdditions: Boolean(closing?.progress?.assistantNoMoreAdditions),
      yearClosed,
      taxSubmitted,
      annualRequired,
      annualSubmitted,
      filingComplete: taxSubmitted && (annualRequired === false || annualSubmitted),
    };
  };

  Object.assign(global, { buildYearEndAssistantFlow, routeForYearEndCheck, workAreaContextForYearEndCheck, taskUsesYearEndSurface, routeUsesYearEndSurface });
})(window);
